import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Badge, DialogActions, DialogTitle, Divider, DeprecatedIconButton, ExternalLink, LinearProgress, List, ListItem, Tooltip } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, MinusCircleIcon, PlusCircleIcon, XmarkCircleIcon } from "@octopusdeploy/design-system-icons";
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { colorScales, space, text, borderRadius, themeTokens, borderWidth } from "@octopusdeploy/design-system-tokens";
import type { CreateGitHubAppConnectionCommand, GitHubAppConnectionResource, GitHubAppInstallation, GitHubRepository, PagedGitHubAppRepositories } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import _ from "lodash";
import type * as React from "react";
import { useRef, useState } from "react";
import { Action } from "~/analytics/Analytics";
import { isGitHubAppTrustedFlow } from "~/areas/configuration/components/Git/getGitHubAppSettings";
import { VerticalDivider } from "~/areas/tenants/components/HeaderBar/CollapsibleFilter";
import { repository } from "~/clientInstance";
import { ListItemContentWithButtons } from "~/components/BorderedListItem";
import BusyFromPromise from "~/components/BusyFromPromise";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { getErrorsFromError, isErrors } from "~/components/DataBaseComponent/Errors";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import ErrorPanel from "~/components/ErrorPanel";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { useTrackedGitConnectionEvent } from "../Analytics/useTrackedGitConnectionEvent";
import { GitHubRepositoryVisibilityChip } from "../ChipList/GitHubChips";
import { GitHubInstallationLogo } from "../GitHubInstallationLogo";
import { GitHubWebAppRegistrationCallout } from "../GitHubWebAppRegistrationCallout";
import type { GitHubRepositoryChangeSummary, SelectedGitHubRepository } from "./useGitHubConnectionRepositorySelectorState";
import { useGitHubConnectionRepositorySelectorState } from "./useGitHubConnectionRepositorySelectorState";
export type PanelRepositorySelectorProps = ExistingConnectionPanelRepositorySelectorProps | NewConnectionPanelRepositorySelectorProps;
interface ExistingConnectionPanelRepositorySelectorProps extends BasePanelRepositorySelectorProps {
    // If the connection is in an error state installation might be undefined. Users can't edit the
    // repository in that state, so it's on the caller to check and not try to render the selector.
    connection: GitHubAppConnectionResource & {
        Installation: GitHubAppInstallation;
    };
}
interface NewConnectionPanelRepositorySelectorProps extends BasePanelRepositorySelectorProps {
    installation: GitHubAppInstallation;
}
interface BasePanelRepositorySelectorProps {
    spaceId: string;
    closeDialog: () => void;
    onSuccess?: () => Promise<boolean>;
}
export default function PanelRepositorySelector({ spaceId, closeDialog, onSuccess, ...props }: PanelRepositorySelectorProps) {
    const installationUrl = useRef(repository.GitHubApp.getInstallationUrl(window.location.href));
    const [showRefreshCallout, setShowRefreshCallout] = useState(false);
    const handleExternalLinkClick = () => setShowRefreshCallout(!isGitHubAppTrustedFlow());
    const isExistingConnection = "connection" in props;
    const initialRepositories = isExistingConnection ? props.connection.Repositories : undefined;
    const installation = isExistingConnection ? props.connection.Installation : props.installation;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResult, setSearchResult] = useState<PagedGitHubAppRepositories>();
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const trackAction = useTrackedGitConnectionEvent();
    const navigation = useSpaceAwareNavigation();
    const { selectedRepositories, summary, selectRepository, removeRepository, repositoryIsSelected } = useGitHubConnectionRepositorySelectorState(initialRepositories);
    const canSave = !summary.nothingSelected && summary.hasChanges;
    useDoBusyTaskEffect(doBusyTask, async () => {
        const result = await repository.GitHubApp.searchRepositories(installation.AccountLogin, searchTerm, 0, 30);
        setSearchResult(result);
    }, [installation.AccountLogin, searchTerm]);
    async function handleSave() {
        await doBusyTask(async () => {
            const repositoriesToSave = selectedRepositories.filter((repo) => repo.action === "Adding" || repo.action === "Existing");
            const repositoryIdsToSave = _.uniq(repositoriesToSave.map((repo) => repo.RepositoryId));
            if (isExistingConnection) {
                await trackAction("Save GitHub connection in modal", Action.Save, async () => {
                    await repository.GitHubApp.modifyConnection(props.connection.Id, { RepositoryIds: repositoryIdsToSave });
                });
            }
            else {
                await trackAction("Create GitHub connection in modal", Action.Add, async () => {
                    const command: CreateGitHubAppConnectionCommand = {
                        InstallationId: installation.InstallationId,
                        RepositoryIds: repositoryIdsToSave,
                    };
                    const response = await repository.GitHubApp.addConnection(command);
                    navigation.navigate(links.gitHubConnectionPage.generateUrl({ spaceId, connectionId: response.ConnectionId }));
                });
            }
        }, {
            onSuccess: async () => {
                closeDialog();
                if (onSuccess) {
                    await onSuccess();
                }
            },
        });
    }
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "column",
            gap: space[8],
            paddingBottom: space[8],
            backgroundColor: themeTokens.color.background.secondary.default,
        }),
        title: css({
            display: "flex",
            alignItems: "center",
            width: "100%",
        }),
        titleAccessory: css({
            font: text.body.bold.medium,
            display: "flex",
            alignItems: "center",
            gap: space[8],
            marginLeft: "auto",
        }),
        panels: css({
            flex: 1,
            minHeight: 0,
            gap: space[8],
            marginLeft: space[8],
            marginRight: space[8],
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
        }),
        panel: css({
            border: `1px solid ${themeTokens.color.border.primary}`,
            borderRadius: borderRadius.small,
            backgroundColor: themeTokens.color.background.primary.default,
        }),
        footer: css({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
        }),
        footerLink: css({
            marginLeft: space[12],
            font: text.body.regular.medium,
        }),
        footerButtons: css({
            display: "flex",
            gap: space[8],
        }),
    };
    const convertedErrors = doBusyTaskStatus.errors === undefined ? undefined : isErrors(doBusyTaskStatus.errors) ? [doBusyTaskStatus.errors] : doBusyTaskStatus.errors.map(getErrorsFromError);
    const title = isExistingConnection ? "Edit Repositories" : "Select Repositories";
    const saveLabel = isExistingConnection ? "Save" : "Create";
    const saveButton = <ActionButton key="Save" label={saveLabel} disabled={!canSave || doBusyTaskStatus.isInProgress} type={ActionButtonType.Primary} onClick={handleSave}/>;
    return (<>
            <DialogTitle>
                <div className={styles.title}>
                    {title}
                    <div className={styles.titleAccessory}>
                        {installation.AccountLogin}
                        <GitHubInstallationLogo installation={installation} small/>
                    </div>
                </div>
            </DialogTitle>
            {showRefreshCallout && <GitHubWebAppRegistrationCallout onClose={() => setShowRefreshCallout(false)}/>}
            <BusyFromPromise promise={doBusyTaskStatus.isInProgress}>{(isBusy: boolean) => <LinearProgress variant={"indeterminate"} show={isBusy}/>}</BusyFromPromise>
            <div className={styles.container}>
                <div>
                    {convertedErrors &&
            convertedErrors.map((error, index) => (<ErrorPanel key={index} errors={error.errors} message={error.message} parsedHelpLinks={error.parsedHelpLinks} helpText={error.helpText} helpLink={error.helpLink} statusCode={error.statusCode}/>))}
                </div>
                <div className={styles.panels}>
                    <div className={styles.panel}>
                        <SearchRepositoryList searchTerm={searchTerm} setSearchTerm={setSearchTerm} searchResult={searchResult} repositoryIsSelected={repositoryIsSelected} selectRepository={selectRepository}/>
                    </div>
                    <div className={styles.panel}>
                        <SelectedRepositoryList summary={summary} selectedRepositories={selectedRepositories} removeRepository={removeRepository} existingConnection={isExistingConnection}/>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerLink}>
                    If you cannot find your repository{" "}
                    <ExternalLink openInSelf={isGitHubAppTrustedFlow()} href={installationUrl.current} onClick={handleExternalLinkClick}>
                        configure repository access on GitHub
                    </ExternalLink>
                </div>
                <DialogActions>
                    <div className={styles.footerButtons}>
                        <ActionButton key="Cancel" label="Cancel" type={ActionButtonType.Secondary} keyboardFocused={true} onClick={() => closeDialog()}/>
                        {summary.nothingSelected ? <Tooltip content={"You must select at least one repository"}>{saveButton}</Tooltip> : saveButton}
                    </div>
                </DialogActions>
            </div>
        </>);
}
interface ScrollingListProps {
    children: React.ReactNode;
}
function ScrollingList({ children }: ScrollingListProps) {
    const style = {
        container: css({
            height: "500px",
            overflowY: "scroll",
        }),
    };
    return <div className={style.container}>{children}</div>;
}
interface SearchRepositoryListProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    searchResult: PagedGitHubAppRepositories | undefined;
    repositoryIsSelected: (repo: GitHubRepository) => boolean;
    selectRepository: (repo: GitHubRepository) => void;
}
function SearchRepositoryList({ searchTerm, setSearchTerm, searchResult, repositoryIsSelected, selectRepository }: SearchRepositoryListProps) {
    const hasMoreResults = searchResult ? searchResult.NumberOfPages > 1 : false;
    const styles = {
        prompt: css({
            padding: space[16],
            borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
            font: text.body.regular.small,
        }),
    };
    return (<>
            <ListHeader title="Add repositories" accessory={<FilterSearchBox value={searchTerm} onChange={setSearchTerm} debounceDelay={600} placeholder={`Search by name...`}/>}/>
            {searchResult && (<ScrollingList>
                    <>
                        <List items={searchResult.Repositories} rowKey={(x) => x.RepositoryId} renderRow={({ item }) => {
                return <SearchResultRepository selected={repositoryIsSelected(item)} repository={item} onClick={selectRepository}/>;
            }} empty={<RepositoryListEmptyState title="No repositories found"/>}/>
                        {hasMoreResults && (<div className={styles.prompt}>
                                <>
                                    Showing {searchResult.Repositories.length} of {searchResult.TotalResults} matching repositories. Refine your search to view other repositories.
                                </>
                            </div>)}
                    </>
                </ScrollingList>)}
        </>);
}
interface SelectedRepositoryListProps {
    summary: GitHubRepositoryChangeSummary;
    selectedRepositories: SelectedGitHubRepository[];
    removeRepository: (repository: SelectedGitHubRepository) => void;
    existingConnection: boolean;
}
function SelectedRepositoryList({ summary, selectedRepositories, removeRepository, existingConnection }: SelectedRepositoryListProps) {
    const styles = {
        badges: css({
            display: "flex",
            gap: space[2],
        }),
    };
    function getSummary() {
        const size = "small";
        if (summary.nothingSelected) {
            return "No repositories selected";
        }
        else if (!summary.hasChanges) {
            return "Select repositories to add or remove";
        }
        else {
            return (<div className={styles.badges}>
                    {summary.adding ? <Badge size={size} count={summary.adding} label="to add" variant="info"/> : undefined}
                    {summary.removing ? <Badge size={size} count={summary.removing} label="to remove" variant="danger"/> : undefined}
                </div>);
        }
    }
    return (<>
            <ListHeader title="Selected repositories" subtitle={getSummary()}/>
            <ScrollingList>
                <List items={selectedRepositories} rowKey={(x) => x.RepositoryId} renderRow={({ item }) => <SelectedRepository repository={item} onClick={removeRepository} hideBadge={!existingConnection}/>}/>
            </ScrollingList>
        </>);
}
interface ListHeaderProps {
    title: string;
    accessory?: React.ReactNode;
    subtitle?: React.ReactNode | string;
}
function ListHeader({ title, accessory, subtitle }: ListHeaderProps) {
    const styles = {
        container: css({
            padding: space[16],
            height: space[72],
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
        }),
        titleContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[16],
        }),
        title: css({
            font: text.body.bold.large,
            color: themeTokens.color.text.primary,
        }),
        subtitle: css({
            font: text.body.regular.small,
            color: themeTokens.color.text.secondary,
            minHeight: space[24],
        }),
    };
    return (<>
            <div className={styles.container}>
                <div>
                    <div className={styles.titleContainer}>
                        <div className={styles.title}>{title}</div>
                        {accessory ? <VerticalDivider /> : null}
                        {accessory}
                    </div>
                    {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
                </div>
            </div>
            <Divider />
        </>);
}
interface SearchResultRepositoryProps {
    repository: GitHubRepository;
    onClick: (repository: GitHubRepository) => void;
    selected: boolean;
}
function SearchResultRepository({ repository, onClick, selected }: SearchResultRepositoryProps) {
    let icon = <AddIcon />;
    let tooltip: string | undefined = undefined;
    if (!repository.IsAdmin) {
        icon = <DeactivatedAddIcon />;
        tooltip = "You are not an administrator on this repository";
    }
    else if (selected) {
        icon = <AddedIcon />;
        tooltip = "This repository has already been selected";
    }
    return <RepositoryListItem repository={repository} buttonIcon={icon} onClick={() => onClick(repository)} disabled={!repository.IsAdmin || selected} dimmed={!repository.IsAdmin} buttonTooltip={tooltip}/>;
}
interface SelectedRepositoryProps {
    hideBadge?: boolean;
    repository: SelectedGitHubRepository;
    onClick: (repository: SelectedGitHubRepository) => void;
}
function SelectedRepository({ repository, onClick, hideBadge }: SelectedRepositoryProps) {
    const icon = repository.action === "Existing" || repository.action === "Adding" ? <RemoveIcon /> : <CancelRemoveIcon />;
    const badge = repository.action === "Existing" ? undefined : repository.action === "Adding" ? "Add" : "Remove";
    return <RepositoryListItem repository={repository} buttonIcon={icon} onClick={() => onClick(repository)} strike={repository.action === "Removing"} badge={hideBadge ? undefined : badge}/>;
}
type RepositoryBadge = "Add" | "Remove";
interface RepositoryListItemProps {
    repository: GitHubRepository;
    buttonIcon: React.ReactElement;
    onClick: () => void;
    badge?: RepositoryBadge;
    buttonTooltip?: string;
    disabled?: boolean;
    strike?: boolean;
    dimmed?: boolean;
}
function RepositoryListItem({ repository, buttonIcon, onClick, badge, buttonTooltip, disabled, strike, dimmed }: RepositoryListItemProps) {
    const styles = {
        item: css({
            opacity: dimmed ? 0.5 : 1,
        }),
        title: css({
            font: text.body.regular.medium,
            color: themeTokens.color.text.primary,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
        }),
        name: css({
            textDecoration: strike ? "line-through" : undefined,
        }),
    };
    let button = <DeprecatedIconButton customIcon={buttonIcon} onClick={onClick} disabled={disabled}/>;
    if (buttonTooltip) {
        button = <Tooltip content={buttonTooltip}>{button}</Tooltip>;
    }
    return (<ListItem>
            <div className={styles.item}>
                <ListItemContentWithButtons content={<div className={styles.title}>
                            <span>
                                {badge && <Badge size="small" variant={badge === "Add" ? "info" : "danger"} label={badge}/>} <span className={styles.name}>{repository.RepositoryName}</span>
                            </span>
                            <GitHubRepositoryVisibilityChip repository={repository}/>
                        </div>} buttons={button}/>
            </div>
        </ListItem>);
}
interface RepositoryListEmptyStateProps {
    title: string;
    subtitle?: React.ReactNode | string;
}
function RepositoryListEmptyState({ title, subtitle }: RepositoryListEmptyStateProps) {
    const styles = {
        container: css({
            padding: space[16],
            textAlign: "center",
        }),
        title: css({
            font: text.body.regular.large,
            color: themeTokens.color.text.primary,
        }),
        subtitle: css({
            font: text.body.regular.medium,
            color: themeTokens.color.text.secondary,
        }),
    };
    return (<div className={styles.container}>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>);
}
function RemoveIcon() {
    return <MinusCircleIcon size={24} color={colorScales.red[500]}/>;
}
function AddIcon() {
    return <PlusCircleIcon size={24} color={colorScales.blue[500]}/>;
}
function DeactivatedAddIcon() {
    return <PlusCircleIcon size={24} color={colorScales.grey[500]}/>;
}
function AddedIcon() {
    return <CheckCircleIcon size={24} color={colorScales.grey[500]}/>;
}
function CancelRemoveIcon() {
    return <XmarkCircleIcon size={24} color={colorScales.blue[500]}/>;
}
